/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { MapsService } from '../../services/maps-service';
import { leadService } from '../../services/lead-service';

export interface ScanUpdate {
  progress: number;
  status: string;
  leads: any[];
  isScanning: boolean;
  newLeadsCount?: number;
}

export class DeepScanManager {
  private mapsService = MapsService.getInstance();
  private gridRectangles: any[] = [];
  private isScanning = false;

  constructor(private onUpdate: (update: ScanUpdate) => void) {}

  public stop() {
    this.isScanning = false;
  }

  public clearMap() {
    this.mapsService.clearAllMarkers();
    for (const rect of this.gridRectangles) rect.setMap(null);
    this.gridRectangles = [];
  }

  private addMarker(place: any) {
    const marker = this.mapsService.addMarker({
      position: place.geometry.location,
      title: place.name,
    });
    marker.addListener('click', () => {
      window.dispatchEvent(new CustomEvent('lead-marker-clicked', { detail: place }));
    });
    return marker;
  }

  async performMultiKeywordScan(
    keywords: string[], 
    city: string, 
    mode: string = 'standard', 
    currentLeads: any[], 
    location?: {lat: number, lng: number}, 
    explicitBounds?: any,
    onKeywordChange?: (kw: string, index: number, total: number) => void
  ) {
    this.isScanning = true;
    const google = this.mapsService.google;
    
    this.onUpdate({ progress: 0, status: explicitBounds ? `Escaneando área personalizada...` : `Mapeando ${city}...`, leads: currentLeads, isScanning: true });
    this.clearMap();

    return new Promise<void>((resolve) => {
      const startScan = async (bounds: any) => {
        this.mapsService.map.fitBounds(bounds);
        const listener = google.maps.event.addListenerOnce(this.mapsService.map, 'idle', () => {
          if (this.mapsService.map.getZoom() > 14) {
            this.mapsService.map.setZoom(14);
          }
        });

        // Grid calculation
        const numQuadrants = mode === 'micro' ? 16 : mode === 'exhaustive' ? 8 : 4;
        const quadrants = [];
        const sw = bounds.getSouthWest();
        const ne = bounds.getNorthEast();
        const latStep = (ne.lat() - sw.lat()) / numQuadrants;
        const lngStep = (ne.lng() - sw.lng()) / numQuadrants;

        for (let i = 0; i < numQuadrants; i++) {
          for (let j = 0; j < numQuadrants; j++) {
            const bSw = new google.maps.LatLng(sw.lat() + latStep * i, sw.lng() + lngStep * j);
            const bNe = new google.maps.LatLng(sw.lat() + latStep * (i + 1), sw.lng() + lngStep * (j + 1));
            const rectBounds = new google.maps.LatLngBounds(bSw, bNe);
            quadrants.push(rectBounds);

            const rect = new google.maps.Rectangle({
              bounds: rectBounds,
              map: this.mapsService.map,
              strokeColor: '#FF0000',
              strokeOpacity: 0.5,
              strokeWeight: 1,
              fillColor: '#FF0000',
              fillOpacity: 0.05,
              clickable: false
            });
            this.gridRectangles.push(rect);
          }
        }

        let seenPlaceIds = new Set(currentLeads.map(l => l.place_id));
        let updatedLeads = [...currentLeads];
        const initialCount = currentLeads.length;

        // Itera por todos os termos estratégicos mapeados
        const effectiveKeywords = keywords && keywords.length > 0 ? keywords : [city];
        const totalKeywords = effectiveKeywords.length;

        for (let k = 0; k < totalKeywords; k++) {
          if (!this.isScanning) break;
          const currentKw = effectiveKeywords[k];
          
          if (onKeywordChange) {
            onKeywordChange(currentKw, k + 1, totalKeywords);
          }

          // Reseta a cor dos blocos para o novo termo
          this.gridRectangles.forEach(r => {
            r.setOptions({ strokeColor: '#1a73e8', fillColor: '#1a73e8', fillOpacity: 0.05, strokeWeight: 1 });
          });

          await this.processQuadrants(
            quadrants, 
            currentKw, 
            seenPlaceIds, 
            updatedLeads, 
            bounds, 
            k + 1, 
            totalKeywords, 
            currentKw
          );

          // Delay inteligente anti-burst entre termos (350ms - 550ms com jitter)
          if (k < totalKeywords - 1 && this.isScanning) {
            const jitterDelay = 350 + Math.floor(Math.random() * 200);
            await new Promise(r => setTimeout(r, jitterDelay));
          }
        }

        this.isScanning = false;
        const newLeadsCount = updatedLeads.length - initialCount;
        this.onUpdate({ progress: 100, status: 'Varredura Concluída!', leads: updatedLeads, isScanning: false, newLeadsCount });
        resolve();
      };

      if (explicitBounds) {
        startScan(explicitBounds);
        return;
      }

      // Tenta geocodificar o nome exato com restrição ao Brasil
      this.mapsService.geocoder.geocode({ 
        address: `${city}, Brasil`,
        componentRestrictions: { country: 'BR' }
      }, (results: any, status: any) => {
        if (status === 'OK' && results && results[0].geometry.bounds) {
          startScan(results[0].geometry.bounds);
        } else if (status === 'OK' && results && results[0].geometry.viewport) {
           startScan(results[0].geometry.viewport);
        } else if (location) {
          // Fallback para ponto central com offset de ~1.5km
          const offset = 0.015;
          const bounds = new google.maps.LatLngBounds(
            new google.maps.LatLng(location.lat - offset, location.lng - offset),
            new google.maps.LatLng(location.lat + offset, location.lng + offset)
          );
          startScan(bounds);
        } else {
          this.onUpdate({ progress: 0, status: `Região não encontrada: ${city}`, leads: currentLeads, isScanning: false });
          resolve();
        }
      });
    });
  }

  async performScan(query: string, city: string, mode: string = 'standard', currentLeads: any[], location?: {lat: number, lng: number}, explicitBounds?: any) {
    return this.performMultiKeywordScan([query], city, mode, currentLeads, location, explicitBounds);
  }

  private async processQuadrants(
    quadrants: any[], 
    query: string, 
    seenPlaceIds: Set<string>, 
    updatedLeads: any[], 
    bounds: any,
    currentTermIndex: number = 1,
    totalTerms: number = 1,
    currentTermName: string = ''
  ) {
    // Varredura ultra-rápida em lotes paralelos de 4 blocos
    const BATCH_SIZE = 4;
    const total = quadrants.length;
    let completedCount = 0;

    for (let i = 0; i < total; i += BATCH_SIZE) {
      if (!this.isScanning) break;

      const batchIndices: number[] = [];
      for (let b = i; b < Math.min(i + BATCH_SIZE, total); b++) {
        batchIndices.push(b);
      }

      // Destacar blocos do lote atual em laranja (em varredura)
      batchIndices.forEach(q => {
        this.gridRectangles[q]?.setOptions({ strokeColor: '#FFA500', fillColor: '#FFA500', fillOpacity: 0.3, strokeWeight: 2 });
      });

      const termInfo = totalTerms > 1 ? `[Termo ${currentTermIndex}/${totalTerms}: "${currentTermName}"] ` : '';
      const overallProgress = totalTerms > 1 
        ? (((currentTermIndex - 1) / totalTerms) * 100) + ((completedCount / total) * (100 / totalTerms))
        : (completedCount / total) * 100;

      this.onUpdate({ 
        progress: overallProgress, 
        status: `${termInfo}Aspirando blocos ${i + 1} a ${Math.min(i + BATCH_SIZE, total)} de ${total}... (${updatedLeads.length} leads)`, 
        leads: updatedLeads, 
        isScanning: true 
      });

      const countBeforeBatch = updatedLeads.length;

      // Execução Paralela dos blocos do lote com micro-delay anti-burst
      await Promise.all(
        batchIndices.map(async (q) => {
          if (!this.isScanning) return;
          const quadrant = quadrants[q];
          await this.searchQuadrant(query, quadrant, seenPlaceIds, updatedLeads, bounds);
          // Marcar bloco concluído em verde
          this.gridRectangles[q]?.setOptions({ strokeColor: '#0F9D58', fillColor: '#0F9D58', fillOpacity: 0.15, strokeWeight: 1 });
        })
      );

      completedCount += batchIndices.length;
      const newLeadsInThisBatch = updatedLeads.length - countBeforeBatch;

      this.onUpdate({ 
        progress: overallProgress, 
        status: `${termInfo}Blocos ${i + 1} a ${Math.min(i + BATCH_SIZE, total)} finalizados. (${updatedLeads.length} leads total)`, 
        leads: updatedLeads, 
        isScanning: true,
        newLeadsCount: newLeadsInThisBatch
      });

      // Micro-intervalo randômico entre lotes de blocos para proteção anti-rate limit
      if (i + BATCH_SIZE < total && this.isScanning) {
        const batchJitter = 150 + Math.floor(Math.random() * 100);
        await new Promise(r => setTimeout(r, batchJitter));
      }
    }
  }

  private async searchQuadrant(query: string, bounds: any, seenPlaceIds: Set<string>, currentLeads: any[], totalBounds: any) {
    const google = this.mapsService.google;
    const center = bounds.getCenter();
    const radius = google.maps.geometry.spherical.computeDistanceBetween(center, bounds.getNorthEast());

    const { results } = await this.mapsService.textSearch(query, center, radius);
    
    for (const place of results) {
      if (!seenPlaceIds.has(place.place_id)) {
        const isAlreadyInSaved = leadService.exists(place.place_id);
        
        // Strict spatial filtering with a small buffer (approx 1km)
        const loc = place.geometry.location;
        const lat = typeof loc.lat === 'function' ? loc.lat() : loc.lat;
        const lng = typeof loc.lng === 'function' ? loc.lng() : loc.lng;
        
        const point = new google.maps.LatLng(lat, lng);
        const buffer = 0.01;
        const extendedBounds = new google.maps.LatLngBounds(
          new google.maps.LatLng(totalBounds.getSouthWest().lat() - buffer, totalBounds.getSouthWest().lng() - buffer),
          new google.maps.LatLng(totalBounds.getNorthEast().lat() + buffer, totalBounds.getNorthEast().lng() + buffer)
        );

        if (extendedBounds.contains(point)) {
          // Poluição Zero: Se o lead já existe e pertence a OUTRA campanha, não mostramos no mapa desta varredura
          const existingLeads = leadService.getLeads();
          const existingLead = existingLeads.find(l => l.place_id === place.place_id);
          
          if (existingLead && existingLead.campaignId) {
             // Se estamos em uma varredura de campanha, e o lead já pertence a outra, omitimos do mapa
             continue; 
          }

          seenPlaceIds.add(place.place_id);
          this.addMarker(place);
          
          if (!isAlreadyInSaved) {
            place.isDeepScanned = true;
            place.scannedAt = new Date().toISOString();
            currentLeads.push(place);
          } else {
            // Se já existe, apenas marcamos que foi visto nesta varredura para o contador de markers,
            // mas não re-adicionamos ao processamento de novos leads se for uma campanha estrita.
            // Porém, para feedback visual, retornamos na lista de 'leads' da varredura.
            place.isExisting = true;
            currentLeads.push(place);
          }
        }
      }
    }
  }
}
